import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { applicationCodes } from 'src/environments/application-codes';
import {
    AnneeReferenceBaseModel,
    AnneeReferenceDto,
    ArrayOfAnneeReferenceBaseModel,
    ArrayOfAnneeReferenceDto,
    ArrayOfChampListeValeurChampListeValeurModel,
    ArrayOfMoteurRegleTypeDateRefDto,
    ChampListeValeurChampListeValeurModel,
    MoteurRegleTypeDateRefDto,
    MutationGetListeAnneeReferenceArgs,
    MutationGetListeAnneesReferenceLightArgs,
    MutationGetListeChampValeurByCodeChampArgs,
    MutationGetListeMoteurRegleTypeDateRefArgs,
} from 'src/graphql/generated-types';
import gql from 'graphql-tag';
import { tap } from 'rxjs/operators';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { Injectable } from '@angular/core';
import {
    SharedGetPeriodsListAction,
    SharedGetReferenceMotorDatesListAction,
    SharedGetReferenceYearsListAction, SharedLightGetReferenceYearsListAction
} from 'src/app/_shared/stores/reference-years/reference-years.action';
import { arrayNotEmpty } from 'src/app/utils/array.utils';

export class ReferenceYearsStateModel {

    referenceYearsList: AnneeReferenceDto[] = null;
    lightReferenceYearsList: AnneeReferenceBaseModel[] = null;

    referenceMotorDatesList: MoteurRegleTypeDateRefDto[] = [];

    periodsList: ChampListeValeurChampListeValeurModel[] = [];
}

@Injectable()
@State<ReferenceYearsStateModel>({
    name: 'referenceYears',
    defaults: new ReferenceYearsStateModel()
})
export class ReferenceYearsState {

    constructor(private readonly graphqlService: GraphqlService) {
    }

    // <editor-fold defaultstate="collapsed" desc="Selectors">

    @Selector()
    public static referenceYearsList(state: ReferenceYearsStateModel): AnneeReferenceDto[] {
        return state.referenceYearsList || [];
    }

    @Selector()
    public static lightReferenceYearsList(state: ReferenceYearsStateModel): AnneeReferenceBaseModel[] {
        return state.lightReferenceYearsList || [];
    }

    @Selector()
    public static referenceMotorDatesList(state: ReferenceYearsStateModel): MoteurRegleTypeDateRefDto[] {
        return state.referenceMotorDatesList;
    }

    @Selector()
    public static periodsList(state: ReferenceYearsStateModel): ChampListeValeurChampListeValeurModel[] {
        return state.periodsList;
    }

    // </editor-fold>

    // <editor-fold defaultstate="collapsed" desc="Actions">

    @Action(SharedGetReferenceYearsListAction)
    getReferenceYearsList({patchState, getState}: StateContext<ReferenceYearsStateModel>): Observable<ArrayOfAnneeReferenceDto> {
        if (arrayNotEmpty(getState()?.referenceYearsList)) {
            return of(null);
        }
        return this.graphqlService.mutateOne<'GetListeAnneeReference', MutationGetListeAnneeReferenceArgs>({
            mutation: gql`
                mutation GetListeAnneeReference(
                    $contexte: ContextInfosInput,
                    $criteres: CriteresAnneeReferenceDTOInput
                ) {
                    GetListeAnneeReference(
                        contexte: $contexte
                        criteres: $criteres
                    ) {
                        AnneeReferenceDTO {
                            Id
                            Code
                            IsArchive
                            DateDeb
                            DateFin
                        }
                    }
                }
            `,
            variables: {
                criteres: {}
            }
        }).pipe(
            tap((GetListeAnneeReference) => {
                patchState({
                    referenceYearsList: GetListeAnneeReference?.AnneeReferenceDTO || []
                });
            })
        );
    }

@Action(SharedLightGetReferenceYearsListAction)
    getLightReferenceYearsList({patchState, getState}: StateContext<ReferenceYearsStateModel>): Observable<ArrayOfAnneeReferenceBaseModel> {
        if (arrayNotEmpty(getState()?.lightReferenceYearsList)) {
            return of(null);
        }

        return this.graphqlService.mutateOne<'GetListeAnneesReferenceLight', MutationGetListeAnneesReferenceLightArgs>({
            mutation: gql`
                mutation GetListeAnneesReferenceLight(
                    $contexte: ContextInfosInput,
                ) {
                    GetListeAnneesReferenceLight(
                        contexte: $contexte
                    ) {
                        AnneeReferenceBaseModel {
                            Id
                            Valeur
                            IsAnneeRefCourante
                        }
                    }
                }
            `,
        }).pipe(
            tap((GetListeAnneesReferenceLight) => {
                patchState({
                    lightReferenceYearsList: GetListeAnneesReferenceLight?.AnneeReferenceBaseModel || []
                });
            })
        );
    }

    @Action(SharedGetReferenceMotorDatesListAction)
    getReferenceMotorDatesList({patchState}: StateContext<ReferenceYearsStateModel>): Observable<ArrayOfMoteurRegleTypeDateRefDto> {
        return this.graphqlService.mutateOne<'GetListeMoteurRegleTypeDateRef', MutationGetListeMoteurRegleTypeDateRefArgs>({
            mutation: gql`
                mutation GetListeMoteurRegleTypeDateRef(
                    $contexte: ContextInfosInput,
                ) {
                    GetListeMoteurRegleTypeDateRef(
                        contexte: $contexte
                    ) {
                        MoteurRegleTypeDateRefDTO {
                            Id
                            Libelle
                            Code
                        }
                    }
                }
            `,
        }).pipe(
            tap((GetListeMoteurRegleTypeDateRef) => {
                patchState({
                    referenceMotorDatesList: GetListeMoteurRegleTypeDateRef?.MoteurRegleTypeDateRefDTO || []
                });
            })
        );
    }

    @Action(SharedGetPeriodsListAction)
    getPeriods({patchState}: StateContext<ReferenceYearsStateModel>): Observable<ArrayOfChampListeValeurChampListeValeurModel> {
        return this.graphqlService.mutateOne<'GetListeChampValeurByCodeChamp', MutationGetListeChampValeurByCodeChampArgs>({
            mutation: gql`
                mutation ValuesList($contexte: ContextInfosInput, $codeCritere: String) {
                    GetListeChampValeurByCodeChamp(contexte: $contexte codeCritere: $codeCritere) {
                        ChampListeValeurChampListeValeurModel {
                            Id
                            CodeChampValeur
                            Valeur
                        }
                    }
                }
            `,
            variables: {
                codeCritere: applicationCodes.PERIOD
            }
        }).pipe(
            tap(periodList => patchState({periodsList: periodList.ChampListeValeurChampListeValeurModel ?? []}))
        );
    }
}
